'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksSonrasiAlkolAlimininSonuclari() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-sonrasi-alkol-aliminin-sonuclari-bilimsel-etkiler-klinik-bulgular-ve-estetik-riskler';
  const pageTitle = 'Botoks Sonrası Alkol Alımının Sonuçları: Bilimsel Etkiler, Klinik Bulgular ve Estetik Riskler';
  const pageDescription = 'Botoks sonrası alkol alınırsa ne olur? Kas asimetrisi, morarma, ödem ve etki kaybı riskleri hakkında bilimsel ve klinik açıklama.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Sonrası Alkol Alımının Sonuçları', href: '/tr/blog/botoks-sonrasi-alkol-aliminin-sonuclari-bilimsel-etkiler-klinik-bulgular-ve-estetik-riskler' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks sonrası alkol alımının sonuçları"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Bakımı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Sonrası Alkol Alımının Sonuçları: Bilimsel Etkiler, Klinik Bulgular ve Estetik Riskler
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>3 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks, yüz kaslarının hareketini kontrollü biçimde azaltarak kırışıklıkları yumuşatan en güvenli ve etkili medikal estetik yöntemlerinden biridir.
                Ancak sonuçların doğallığı yalnızca uygulamanın kalitesine değil, sonrasında vücudun verdiği biyolojik yanıta da bağlıdır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu süreçte en çok merak edilen konulardan biri:
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
                <p className="text-amber-800 font-medium text-lg">
                  "Botoks yaptırdıktan sonra alkol içsem bir şey olur mu?"
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Cevap net: Evet, olur.
                </p>
                <p className="text-red-800">
                  Alkol, botoksun kas dokusu içindeki dağılımını, damar geçirgenliğini ve iyileşme hızını doğrudan etkiler.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Botoksun Etki Mekanizması
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum toksini, sinir uçlarındaki asetilkolin salınımını engeller.
                Bu sayede hedef kas gevşer, cilt yüzeyi pürüzsüzleşir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Maddenin bu etkisi, kas içi sinir-kas birleşiminde (nöromüsküler kavşak) gerçekleşir ve ilk 48 saat içinde sabitlenir.
                İşte tam bu süreçte alkol devreye girerse, fizyolojik denge bozulur.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Alkolün Biyokimyasal Etkisi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Mekanizma</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Vazodilatasyon</td>
                      <td className="border border-gray-300 px-4 py-3">Damarlar genişler, kas çevresine fazla kan akışı olur</td>
                      <td className="border border-gray-300 px-4 py-3">Botoksun yayılım riski artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kan viskozitesinin azalması</td>
                      <td className="border border-gray-300 px-4 py-3">Pıhtılaşma gecikir</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma olasılığı yükselir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıvı-elektrolit dengesizliği</td>
                      <td className="border border-gray-300 px-4 py-3">Hücre dışı sıvı artar</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem gelişir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas gevşetici etki (etanol)</td>
                      <td className="border border-gray-300 px-4 py-3">Kas tonusu değişir</td>
                      <td className="border border-gray-300 px-4 py-3">Asimetrik sonuç riski artar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Karaciğer metabolizması yükü</td>
                      <td className="border border-gray-300 px-4 py-3">Botoks etkisi geç oturabilir</td>
                      <td className="border border-gray-300 px-4 py-3">Sonuçlar geç belirginleşir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  💬 Botoksun milimetrik etkisine karşı, alkol makro ölçekte dengesizlik yaratır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Klinik Olarak Ne Görülür?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morarma (Ekimoz)</td>
                      <td className="border border-gray-300 px-4 py-3">2-5 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Damar sızıntısı sonucu küçük lekeler oluşur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik / Ödem</td>
                      <td className="border border-gray-300 px-4 py-3">1-3 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Damar geçirgenliği artışıyla sıvı tutulumu</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas asimetrisi</td>
                      <td className="border border-gray-300 px-4 py-3">3-7 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Botoksun farklı kaslara yayılmasıyla dengesizlik</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Etkide gecikme</td>
                      <td className="border border-gray-300 px-4 py-3">5-10 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Botulinum molekülünün geç bağlanması</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Geçici göz kapağı düşüklüğü (Ptozis)</td>
                      <td className="border border-gray-300 px-4 py-3">1-2 hafta</td>
                      <td className="border border-gray-300 px-4 py-3">Nadir ama olası komplikasyon</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Bilimsel Açıklama: Alkol ve Nöromüsküler İletim
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Etanol, sinir hücrelerinde sodyum-potasyum kanallarının işleyişini etkiler.
                Bu durum kas gevşemesini artırır, ancak botoksun hedeflediği kas kontrolünü geçici olarak dengesizleştirir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Özellikle alın ve göz çevresi gibi kas yoğunluğu yüksek bölgelerde, alkol sonrası geçici kas seğirmeleri veya asimetriler gözlenebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Zaman Çizelgesi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Zaman Aralığı</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Ne Olur</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">0–6 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Botoks molekülü difüze olur</td>
                      <td className="border border-gray-300 px-4 py-3">En yüksek risk</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">6–24 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Reseptör bağlanması başlar</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">24–48 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Stabilizasyon dönemi</td>
                      <td className="border border-gray-300 px-4 py-3">Alkol kesinlikle yasak</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">48 saat sonrası</td>
                      <td className="border border-gray-300 px-4 py-3">Kas reseptörleri sabitlenir</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük risk</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu nedenle ilk 48 saatlik dönem "koruma penceresi" olarak kabul edilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Vaka Örneği
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam işlemden aynı akşam iki kadeh şarap içtiğini söyledi.
                  48 saat sonra kaşlar arasında hafif dengesizlik gelişti.
                  10 gün içinde kısmen düzeldi ancak küçük bir rötuş gerekmişti.
                  Alkol almayan benzer vakalarda bu durum hiç gözlenmedi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Alkol Türüne Göre Etki Düzeyi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İçecek</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gözlenen Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şarap / Bira</td>
                      <td className="border border-gray-300 px-4 py-3">Orta düzey vazodilatasyon</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma, ödem</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Distile (vodka, viski)</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek alkol oranı</td>
                      <td className="border border-gray-300 px-4 py-3">Kas seğirmesi, dengesizlik</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kokteyl (şekerli)</td>
                      <td className="border border-gray-300 px-4 py-3">Şeker + alkol kombinasyonu</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik, su tutulumu</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Az alkollü içecekler</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük etki</td>
                      <td className="border border-gray-300 px-4 py-3">Yine de önerilmez</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Botoksun Etkisini Azaltan Kombinasyonlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etkileşim</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sigara</td>
                      <td className="border border-gray-300 px-4 py-3">Damar daralması + toksik etki</td>
                      <td className="border border-gray-300 px-4 py-3">Etkinin süresi kısalır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Aşırı kahve / enerji içecekleri</td>
                      <td className="border border-gray-300 px-4 py-3">Adrenalin artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Kas kasılması artar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağır spor</td>
                      <td className="border border-gray-300 px-4 py-3">Kan basıncı yükselir</td>
                      <td className="border border-gray-300 px-4 py-3">Botoks yayılımı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sauna / sıcak duş</td>
                      <td className="border border-gray-300 px-4 py-3">Isı artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem riski</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu kombinasyonlar alkolle birlikte riskleri katlar.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Psikolojik ve Estetik Etki
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun temel amacı yüz ifadesini "daha dinlenmiş" hale getirmektir.
                Alkol sonrası oluşan ödem ve morarma, bu etkiyi tersine çevirebilir.
                Kişi aynada "daha yorgun ve şiş" bir görünümle karşılaşabilir.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Estetik müdahale sadece görünüş değil, ruh halidir; kısa süreli keyif, uzun süreli tatmini gölgelememelidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sonuçların Kalıcılığına Etki
              </h2>
              
              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <p className="text-amber-800 font-medium mb-4">Botoks Etki Süresi Karşılaştırması</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-amber-100 rounded-lg p-4">
                      <p className="text-amber-900 font-semibold">Normal Kullanım</p>
                      <p className="text-amber-800 text-2xl font-bold">4-6 ay</p>
                    </div>
                    <div className="bg-red-100 rounded-lg p-4">
                      <p className="text-red-900 font-semibold">Yoğun Alkol Kullanımı</p>
                      <p className="text-red-800 text-2xl font-bold">3 ay</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoksun etki süresi ortalama 4-6 aydır.
                Ancak yoğun alkol tüketimi, karaciğer metabolizmasını hızlandırarak toksinin vücuttan daha hızlı uzaklaşmasına neden olabilir.
                Bu durumda etki süresi 3 aya kadar düşebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Etik ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik uygulamalarda hekimin görevi yalnızca "güzelleştirmek" değil, vücudu güvenli sınırlar içinde desteklemektir.
                Hastanın bu sürece özen göstermesi, hem hekim emeğine hem de kendi sağlığına saygı göstergesidir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6 mb-6">
                <p className="text-purple-800 italic">
                  "Botoks bir sanat, vücut ise tuvaldir.
                  Tuvale zarar verecek hiçbir alışkanlık, sanatı koruyamaz."
                </p>
                <p className="text-purple-800 font-medium mt-4">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Botokstan sonra alkol içtim, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Bol su içip başınızı yüksek tutun, sıcak ortamlardan kaçının.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Alkol botoksu tamamen bozar mı?</h3>
                  <p className="text-gray-600">Hayır ama etkinin homojenliğini azaltabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kaç gün sonra alkol alınabilir?</h3>
                  <p className="text-gray-600">En az 48 saat, tercihen 72 saat.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Morarma oldu, ne kadar sürer?</h3>
                  <p className="text-gray-600">Genellikle 3–5 günde geçer; arnika krem destek olabilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Botoks etkisi ne zaman başlar?</h3>
                  <p className="text-gray-600">Normalde 3-7 gün, alkol alındıysa 5-10 gün sürebilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Göz kapağı düşüklüğü olur mu?</h3>
                  <p className="text-gray-600">Nadir görülür, genellikle 1-2 haftada düzelir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks sonrası alkol tüketimi; morarma, ödem, kas asimetrisi ve etki kaybı gibi sonuçlara neden olabilir.
                Kısa bir bekleme süresiyle tüm bu riskler önlenebilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin'in önerisi:
                </p>
                <p className="text-red-800 mt-2">
                  "İlk 48 saat botoksun vücutla uyum sağladığı dönemdir.
                  Bu süre boyunca alkol, sigara ve yoğun egzersizden kaçınmak hem sonucu hem kalıcılığı belirler."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Botulinum Toxin Post-Care Guidelines"</li>
                  <li>ISAPS Global Report (2024) – "Alcohol and Neurotoxin Interactions"</li>
                  <li>Harvard Health Publishing (2023) – "Ethanol and Facial Vascular Dynamics"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Botoks Sonrası Bakım Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#alkol</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#asimetribotoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#morarma</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan botoks uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-sonrasi-alkol-tuketimi-bilimsel-aciklamalar-klinik-etkiler-ve-uzman-onerileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Botoks sonrası alkol içmek neden önerilmez? Dolaşım, kas aktivitesi ve ödem üzerindeki biyolojik etkiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </a>
            <a
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Botoks fiyatlarını etkileyen faktörler ve 2026 güncel fiyat listesi.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
